import React, { useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useThemeColors } from "../../../Store/modules/theme";
import { exportTableToPDF } from "../../../Utils/exportToPDF";

interface HtmlWithTablesProps {
  htmlString: string;
}

export const HtmlWithTables: React.FC<HtmlWithTablesProps> = (props) => {
  const { htmlString } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [theme] = useThemeColors();
  const [loading, setLoading] = useState(false);

  const dateStr = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
  }).format(new Date());

  function injectDownloadLinks(html: string, t: TFunction): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const tables = doc.querySelectorAll("table");

    tables.forEach((table, index) => {
      const wrapper = doc.createElement("div");
      wrapper.style.margin = "25px 0 15px 0px";
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.className = "xs:text-xs md:text-base";

      const scrollWrapper = doc.createElement("div");
      scrollWrapper.className = "table-wrapper";
      scrollWrapper.style.width = "100%";
      scrollWrapper.style.overflowX = "auto";

      const style = document.createElement("style");
      style.textContent = `
        .table-wrapper table {
          border-collapse: collapse;
          width: max-content;
          min-width: 100%;
        }
        .table-wrapper::-webkit-scrollbar {
            height: 4px;
        }

        .table-wrapper::-webkit-scrollbar-track {
            background: #e0e0e0;
        }

        .table-wrapper::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 999px;
        }

        .table-wrapper::-webkit-scrollbar-thumb:hover {
            background: #555;
        }
        thead th {
          background: #292d32;
          color: white;
          font-weight: 400;
          padding: 8px 16px;
          text-align: center;
          white-space: nowrap;
        }
        tbody td {
          padding: 8px 16px;
          text-align: center;
          font-size: 12px;
          line-height: 1.5;
          white-space: nowrap;
        }
        tbody tr:nth-child(even) {
          background-color: #f8f8f8;
        }
        tbody td:first-child {
          background-color: ${theme.showInfoFile};
          font-weight: 300;
        }
      `;

      table.parentNode?.insertBefore(scrollWrapper, table);
      scrollWrapper.appendChild(table);
      scrollWrapper.appendChild(style);

      const label = doc.createElement("span");
      //@ts-ignore
      label.textContent = t("result.toDownloadTable");

      const link = doc.createElement("span");
      // @ts-ignore
      link.innerHTML = `<span class="download-text">${t("result.clickHere")}</span>
          <span class="download-loader hidden ml-2 align-middle">
            <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin inline-block"></span>
          </span>
        `;
      link.style.color = "#478DF5";
      link.style.margin = "0 4px";
      link.style.cursor = "pointer";
      link.className = "global-download";
      link.setAttribute("data-table-index", index.toString());
      wrapper.appendChild(label);
      wrapper.appendChild(link);
      scrollWrapper.parentNode?.insertBefore(wrapper, scrollWrapper);
    });

    return doc.body.innerHTML;
  }

  function getTableColumnCount(tableHTML: string): number {
    const parser = new DOMParser();
    const doc = parser.parseFromString(tableHTML, "text/html");
    const table = doc.querySelector("table");
    if (!table) return 0;

    const headerRow = table.querySelector("tr");
    if (headerRow) {
      const headerCells = headerRow.querySelectorAll("th, td");
      return headerCells.length;
    }

    return 0;
  }

  function generateDailyTablesHTML(html: string): HTMLElement {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const mainTable = doc.querySelector("table");
    if (!mainTable) return doc.body;

    const rows = Array.from(mainTable.querySelectorAll("tbody tr"));
    const headers = Array.from(mainTable.querySelectorAll("thead th")).map(
      (th) => th.textContent || "",
    );

    const wrapper = document.createElement("div");
    wrapper.style.display = "flex";
    wrapper.style.flexDirection = "column";
    wrapper.style.gap = "12px";

    rows.forEach((row, rowIndex) => {
      const cells = Array.from(row.querySelectorAll("td"));
      const miniTable = document.createElement("table");
      miniTable.style.width = "100%";
      miniTable.style.borderCollapse = "collapse";
      miniTable.style.marginBottom = "8px";
      miniTable.setAttribute("dir", "rtl");

      // header
      const thead = document.createElement("thead");
      const titleRow = document.createElement("tr");
      const titleCell = document.createElement("th");
      titleCell.colSpan = 2;
      titleCell.textContent = cells[0].textContent || `روز ${rowIndex + 1}`;

      titleCell.style.background = "#292D32";
      titleCell.style.color = "white";
      titleCell.style.textAlign = "center";
      titleCell.style.fontWeight = "600";
      titleCell.style.fontSize = "16px";
      titleCell.style.paddingBottom = "16px";
      titleCell.style.paddingTop = "0px";
      titleCell.style.border = "none";

      titleRow.appendChild(titleCell);
      thead.appendChild(titleRow);
      miniTable.appendChild(thead);

      // body
      const tbody = document.createElement("tbody");
      const mealLabels = headers.slice(1);

      mealLabels.forEach((label, i) => {
        const tr = document.createElement("tr");
        if (i % 2 === 0) tr.style.background = "#f8f8f8";

        const td1 = document.createElement("td");
        td1.textContent = label;
        td1.style.fontWeight = "bold";
        td1.style.background = "#F0F0F0";
        td1.style.paddingBottom = "20px";

        const td2 = document.createElement("td");
        td2.textContent = cells[i + 1]?.textContent || "-";
        td2.style.paddingBottom = "20px";
        td2.style.borderBottom = "1px solid #F0F0F0";
        td2.className = "p-2 text-gray-800 bg-[#FFFFFF]";

        tr.appendChild(td1);
        tr.appendChild(td2);
        tbody.appendChild(tr);
      });

      miniTable.appendChild(tbody);
      wrapper.appendChild(miniTable);
    });

    return wrapper;
  }

  async function exportSmartVerticalPDF(html: string) {
    const virtualDoc = generateDailyTablesHTML(html);
    virtualDoc.style.position = "fixed";
    document.body.appendChild(virtualDoc);

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginTop = 10;
    const marginSide = 10;
    const gapPx = 120;
    const pxToMm = 0.264583;
    const gapMm = gapPx * pxToMm;

    const tables = Array.from(virtualDoc.querySelectorAll("table"));

    let currentY = marginTop;

    for (let i = 0; i < tables.length; i++) {
      const table = tables[i] as HTMLElement;

      const canvas = await html2canvas(table, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/jpeg");
      const imgWidth = pageWidth - marginSide * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (currentY + imgHeight > pageHeight - marginTop) {
        pdf.addPage();
        currentY = marginTop;
      }

      pdf.addImage(imgData, "jpeg", marginSide, currentY, imgWidth, imgHeight);
      currentY += imgHeight + gapMm;
    }

    document.body.removeChild(virtualDoc);
    const fileName = `ltk-exported-pdf-${dateStr}.pdf`;
    pdf.save(fileName);
  }

  const injectedHtml = useMemo(() => injectDownloadLinks(htmlString, t), [htmlString, t]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = async (e: Event) => {
      const node = e.target as HTMLElement;
      const downloadBtn = node.closest(".global-download") as HTMLElement;
      if (downloadBtn) {
        const text = downloadBtn.querySelector(".download-text") as HTMLElement;
        const loader = downloadBtn.querySelector(".download-loader") as HTMLElement;

        text?.classList.add("hidden");
        loader?.classList.remove("hidden");

        const tableIndex = parseInt(downloadBtn.getAttribute("data-table-index") || "0", 10);
        const tables = containerRef.current?.querySelectorAll("table");
        const targetTable = tables?.[tableIndex] as HTMLTableElement;
        if (!targetTable) return;
        try {
          const dpi = 96;
          const a3WidthPx = (297 / 25.4) * dpi;

          const pageWidthMm = 350;
          const pxToMm = 0.264583;
          const tableWidthMm = targetTable.offsetWidth * pxToMm;

          if (tableWidthMm > pageWidthMm) {
            await exportSmartVerticalPDF(targetTable.outerHTML);
          } else {
            const fileName = `ltk-exported-pdf-${dateStr}.pdf`;
            exportTableToPDF(targetTable, fileName);
          }
          setLoading(true);
        } finally {
          text?.classList.remove("hidden");
          loader?.classList.add("hidden");
          setLoading(false);
        }
      }
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [injectedHtml, htmlString]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className={`response-content-text transition duration-300 ${loading ? "blur-[2px]" : ""}`}
        dangerouslySetInnerHTML={{ __html: injectedHtml }}
      />

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-50 bg-white/60">
          <div className="flex flex-col items-center px-6 py-4 rounded-lg">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
    </div>
  );
};
